import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import { ClientData, ClientSegment } from '@interfaces';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { sanitizeError } from '@shared/helpers/sanitize-error-helper';
import { FlowTypes, urlMapper } from '@shared/models';
import { PhoneValidationStatus } from '@shared/models/phone-number.interface';
import { IDPSState } from '@shared/models/prospect-data-models.interface';
import { LoggerCodes } from '@shared/modules/logger/logger';
import { ClientDataService, ProspectService } from '@shared/services';
import {
  formatCitizenshipData,
  formatProspectAddressData,
} from '@store/reducers/personal-data/personal-data.reducer';
import {
  getIsIndividualAccount,
  getIsInheritanceFlow,
  getIsInheritedIraFlow,
  getIsIRAAccount,
  getIsMultiInheritanceFlow,
  getIsTrustToTrustFlow,
  TriageState,
} from '@store/reducers/triage/triage.reducer';
import { selectPersonalDataState } from '@store/selectors/personal-data/personal-data.selectors';
import { formatPhoneNumber, formatSSN } from '@store/selectors/receipt/cashier-nonretirement';
import { flowTypeSelection, selectTriageState } from '@store/selectors/triage/triage.selectors';
import * as dayjs from 'dayjs';
import { catchError, filter, switchMap, tap, withLatestFrom } from 'rxjs';
import { LoadClientSSNSuccess, UpdatePersonalAddress } from 'vg-interaction-model';

import {
  closeSpinner,
  Go,
  LoadClientAccounts,
  LogError,
  LogInfo,
  openSpinner,
  updateProspectClearDocuments,
} from '../../actions';
import { UpdateMfePersonalInfoClientData } from '../../actions/mfe-personal-info-client/mfe-personal-info-client.actions';
import {
  LoadPersonalData,
  LoadPersonalDataClientFail,
  LoadPersonalDataPayloadSuccess,
  LoadPersonalDataSuccess,
  RedirectToPVR,
  UpdateClientFlagDigitalAdvise,
  UpdateClientSegment,
} from '../../actions/personal-data/personal-data.actions';
import { UpdateMfePersonalAddress } from '../../actions/personal-info-address/personal-info-address.actions';
import { UpdateMfePhoneFromGetData } from '../../actions/personal-phone/personal-phone.actions';
import {
  MultiTriageContinue,
  TriageContinue,
  UpdateClientStatusSelection,
} from '../../actions/triage/triage.actions';
import { State } from '../../reducers';

@Injectable()
export class PersonalDataEffects {
  constructor(
    private actions$: Actions,
    private prospectService: ProspectService,
    private clientDataService: ClientDataService,
    private globalStore: Store<State>,
  ) {}

  loadPersonalData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadPersonalData),
      withLatestFrom(this.globalStore.pipe(select(selectPersonalDataState))),
      filter(([, personalData]) => {
        return Boolean(!personalData.loaded);
      }),
      tap(() => this.globalStore.dispatch(openSpinner())),
      switchMap(() => {
        return this.clientDataService.callClientFlags().pipe(
          withLatestFrom(this.globalStore.pipe(select(selectTriageState))),
          switchMap(([clientFlagsResponse, triage]: [ClientSegment, TriageState]) => {
            // Client is lite registered prospect

            this.globalStore.dispatch(
              UpdateClientFlagDigitalAdvise({ payload: clientFlagsResponse.digitalAdviseService }),
            );
            /** TODO: bring this flag back when we have more guidance: clientFlagsResponse.ugmaUtmaAdultOnly **/
            if (
              clientFlagsResponse.segmentTypeCode === 'PR' ||
              clientFlagsResponse.savings529BeneficiaryOnly ||
              clientFlagsResponse.smallMarket401kAccountOnly
            ) {
              this.globalStore.dispatch(UpdateClientSegment({ payload: 'PR' }));
              this.globalStore.dispatch(UpdateClientStatusSelection({ payload: 'NO' }));

              // Check inheritance flow types
              const isStandardInheritance = getIsInheritanceFlow(triage);
              const isMultiInheritance = getIsMultiInheritanceFlow(triage);
              const isInheritedIRA = getIsInheritedIraFlow(triage);

              const isInAnyInheritanceFlow =
                isStandardInheritance || isMultiInheritance || isInheritedIRA;

              // Check valid account types
              const hasIRAAccount = getIsIRAAccount(triage);
              const hasIndividualAccount = getIsIndividualAccount(triage);
              const hasTrustToTrustFlow = getIsTrustToTrustFlow(triage);

              const hasValidAccountType =
                hasIRAAccount || hasIndividualAccount || hasTrustToTrustFlow;

              // Special inheritance flows bypass account type restrictions
              const hasSpecialInheritanceFlow = isMultiInheritance || isInheritedIRA;

              // Prospect is eligible if in inheritance flow AND (has valid account OR special flow)
              const isEligible =
                isInAnyInheritanceFlow && (hasValidAccountType || hasSpecialInheritanceFlow);

              if (!isEligible) {
                return [
                  Go({ path: [`${urlMapper[triage.flowType]}/ineligible`] }),
                  LogInfo({
                    payload: {
                      eventId: 'ineligible-page-route-personalDataEffect',
                      message: `Prospect has no access to the ${triage.flowType} flow`,
                    },
                  }),
                ];
              }
              this.globalStore.dispatch(new UpdatePersonalAddress({ isStreetAddressSame: false }));

              return this.prospectService.callDPSNode().pipe(
                switchMap((data: IDPSState) => {
                  // redirectToPVR in the response will always be true, if the field exists we need to redirect to PVR
                  if ('redirectToPVR' in data) {
                    return [
                      LogInfo({
                        payload: {
                          eventId: 'personal-data-effect-redirect-pvr',
                          message: 'DPS response contained redirectToPVR field, redirecting to PVR',
                        },
                      }),
                      RedirectToPVR(),
                    ];
                  } else {
                    const formattedProspectAddress = formatProspectAddressData(data);
                    return [
                      LoadPersonalDataSuccess(),
                      ...(data.clearData
                        ? [
                            updateProspectClearDocuments({
                              //only include this in the events array if clearData is explicitly defined.
                              payload: { ...data.clearData, poid: data.poid },
                            }),
                          ]
                        : []),
                      LoadClientSSNSuccess({ payload: formatSSN(data.personalInfo.tin) }),
                      UpdateMfePersonalInfoClientData({
                        payload: {
                          isInformationCorrect: null,
                          personalInfo: {
                            affiliationStartDate: '',
                            firstName: data.personalInfo.firstName,
                            middleName: data.personalInfo.middleName,
                            lastName: data.personalInfo.lastName,
                            email: data.personalInfo.emailAddress,
                            birthDate: dayjs(data.personalInfo.dateOfBirth).format('MM/DD/YYYY'),
                            citizenshipStatus: formatCitizenshipData(data.personalInfo.citizenship),
                            countryOfCitizenship: data.personalInfo.countryOfResidence,
                            gender: data.personalInfo.gender,
                            suffix: data.personalInfo.suffix,
                            ssn: formatSSN(data.personalInfo.tin),
                          },
                        },
                      }),
                      UpdateMfePhoneFromGetData({
                        payload: {
                          phoneNumber: formatPhoneNumber(
                            data.contactInfo.primaryPhoneNumber.number,
                          ),
                          isMobile: data.contactInfo.primaryPhoneNumber.isCellPhone,
                          validationStatus: PhoneValidationStatus.VALID,
                        },
                      }),
                      new UpdatePersonalAddress(formattedProspectAddress),
                      UpdateMfePersonalAddress({
                        payload: {
                          ...formattedProspectAddress,
                          validationStatus: 'VALID',
                          showValidation: false,
                          clientAddresses: {
                            mailingAddressesList: [],
                            streetAddressesList: [],
                          },
                          isClientAddressesProcessing: false,
                          isClientAddressesLoaded: true,
                          mailingAddressDropdown: '',
                          streetAddressDropdown: '',
                          isContinueConfirmed: true,
                        },
                      }),
                      closeSpinner(),
                    ];
                  }
                }),
                catchError((e) => {
                  if (e.status === 404) {
                    return [
                      LogError({
                        payload: {
                          eventId: 'error-page-catch-dps-failure-404',
                          message: `DPS call failed with error: ${JSON.stringify(sanitizeError(e))}`,
                          code: LoggerCodes.NULL_FAILURE,
                        },
                      }),
                      RedirectToPVR(),
                    ];
                  } else {
                    return [
                      LogError({
                        payload: {
                          eventId: 'error-page-catch-dps-failure',
                          message: `DPS call failed with error: ${JSON.stringify(sanitizeError(e))}`,
                          code: LoggerCodes.NULL_FAILURE,
                        },
                      }),
                      LoadPersonalDataClientFail(),
                    ];
                  }
                }),
              );
            } else {
              this.globalStore.dispatch(
                UpdateClientSegment({ payload: clientFlagsResponse.segmentTypeCode }),
              );
              // Client is a full registered client
              this.globalStore.dispatch(UpdateClientStatusSelection({ payload: 'YES' }));

              // Client should be on landing page, continue to call client data
              return this.clientDataService.callClientData().pipe(
                switchMap((clientDataResponse: ClientData) => {
                  return [
                    LoadPersonalDataPayloadSuccess({ payload: clientDataResponse }),
                    LoadPersonalDataSuccess(),
                    LoadClientAccounts(),
                    LoadClientSSNSuccess({ payload: formatSSN(clientDataResponse.tin_private) }),
                    UpdateMfePersonalInfoClientData({
                      payload: {
                        isInformationCorrect: null,
                        personalInfo: {
                          affiliationStartDate: '',
                          firstName: clientDataResponse.firstName,
                          middleName: clientDataResponse.middleName,
                          lastName: clientDataResponse.lastName,
                          email: clientDataResponse.contactInfo.primaryEmailAddress,
                          birthDate: dayjs(clientDataResponse.birthDate).format('MM/DD/YYYY'),
                          citizenshipStatus: formatCitizenshipData(clientDataResponse.citizenship),
                          countryOfCitizenship: '',
                          gender: clientDataResponse.gender,
                          suffix: '',
                          ssn: formatSSN(clientDataResponse.tin_private),
                        },
                      },
                    }),
                  ];
                }),
                catchError(() => {
                  return [
                    LoadPersonalDataClientFail(),
                    LogError({
                      payload: {
                        eventId: 'error-page-catch-personalData',
                        message: 'Client Data Service Error',
                        code: LoggerCodes.CLIENT_DATA_PROCESSING_EXCEPTION,
                      },
                    }),
                  ];
                }),
              );
            }
          }),
          catchError(() => {
            return [
              LoadPersonalDataClientFail(),
              LogError({
                payload: {
                  eventId: 'error-page-catch-client-flags',
                  message: 'Client Flags Service Error',
                  code: LoggerCodes.CLIENT_DATA_PROCESSING_EXCEPTION,
                },
              }),
            ];
          }),
        );
      }),
    ),
  );

  redirectToPVR$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RedirectToPVR),
      withLatestFrom(this.globalStore.pipe(select(flowTypeSelection))),
      switchMap(([, flowType]) => {
        const logPayload = {
          eventId: 'pvr-external-route-personalDataEffect',
          message: 'Failed to retrieve dps data, sending to PVR',
          code: LoggerCodes.GENERAL_SUCCESS,
        };
        if (flowType === FlowTypes.MULTI_INHERITANCE) {
          return [
            // Runs the effect in Triage Summary which creates the PVR cookies and sends them to PVR
            MultiTriageContinue(),
            LogInfo({ payload: logPayload }),
          ];
        } else {
          return [
            // Runs the effect in Triage Summary which creates the PVR cookies and sends them to PVR
            LogInfo({
              payload: {
                eventId: 'pvr-external-route-personalDataEffect-start',
                message: 'PVR redirect initiation when dps returns 404',
              },
            }),
            TriageContinue(),
            LogInfo({ payload: logPayload }),
          ];
        }
      }),
    ),
  );

  clientPersonalDataRetrievalFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadPersonalDataClientFail),
      switchMap(() => {
        return [
          Go({ path: ['error'] }),
          closeSpinner(),
          LogError({
            payload: {
              eventId: 'error-page-route-personalDataEffect',
              message: 'Failed to retrieve personal data',
              code: LoggerCodes.CLIENT_DATA_PROCESSING_EXCEPTION,
            },
          }),
        ];
      }),
    ),
  );
}
